// @flow
import React, {Component, Fragment} from 'react';
import {Helmet} from 'react-helmet-async';
import {compose} from 'redux';
import {connect} from 'react-redux';

import gql from 'graphql-tag';
import {Waypoint} from '@homeaway/react-unveil';
import Experiment from '@homeaway/react-experiment';

import DatesPromptBanner from '../DatesPromptBanner/DatesPromptBanner';
import Hit, {fragments as hitFragments, hitDataErrors} from '../Hit/Hit';
import {
    LeadCarouselContainerWrapper,
    LEAD_CAROUSEL_PLACEMENT
} from '../LeadCarouselContainer';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import PinnedProperty from '../PinnedProperty/PinnedProperty';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import ScrollListener from '../ScrollListener/ScrollListener';
import {isDateless} from '../../utils/searchParamsUtils';
import logger from '../../utils/clientLogger';
import {isDifferent} from '../../utils';
import Timing from '../Timing/Timing';
import {media} from '../../utils/breakpoints';
import {mergeListingsCollectionWithPinnedListing} from '../../utils/mergeListingsCollectionWithPinnedListing';
import {reportSuccess, reportError} from '@homeaway/react-error-reporter';

import type {ListingShape, MouseLeaveShape} from '../Hit/Hit';
import withPAR from '../Timing/withPAR';
import withLingua from '../withLingua/withLingua';
import type {LinguaProps} from '../withLingua/withLingua';
import {MesoAdsCL} from '../MesoAds/MesoAdsCL';
import {triggerListHitClickEDAPEvent} from '../../utils/hitEdapUtils';
import {
    triggerSearchResultSetPresentedEdapEvent,
    triggerSerpHitPresentedEdapEvent,
    triggerSerpHitMabPresentedEdapEvent
} from '../../utils/collectionEdapUtils';
import {HA_PTA_LANDING_CLADSONSERP} from '../../abacus/experiments';

import type {Breakpoint} from '../../utils/breakpoints';
import type {State as ReduxState} from '../../reducers';
import type {SearchParamsType} from '../../reducers/searchParams';

import './HitCollection.less';
import {
    hitThumbnailBackgroundImageClass,
    MAX_PRELOAD_HIT_THUMBNAIL
} from './hitCollectionUtils';
import {listingHasPrice} from '../Price/Price';

import {
    MultiUnitHit,
    triggerMultiUnitHitExposedEDAPEvent
} from '@homeaway/react-multi-units/lib/transpiled/search';
import type {PercentBookedResult} from '../../queries/searchRequestQuery';

export const MOBILE_DATES_PROMPT_BANNER_INDEX = 7;

export const fragments = {
    SearchResult: gql`
        fragment HitCollectionSearchResult on SearchResult {
            page
            pageSize
            queryUUID
            percentBooked {
                currentPercentBooked
            }
            listings {
                ...HitListing
            }
            resultCount
            pinnedListing {
                headline
                listing {
                    ...HitListing
                }
            }
        }
        ${hitFragments.Listing}
    `
};

export type PinnedListing = {
    headline: ?string,
    listing: ListingShape
};

type StateProps = {
    breakpoint: Breakpoint,
    currency?: string,
    directEntry: boolean,
    fetchImages?: string,
    fullLocale: string,
    havId: string,
    inputParams: SearchParamsType,
    isDatelessSearch: boolean,
    isDesktop: boolean,
    isMobile: boolean,
    isTablet: boolean,
    isGoodBot: boolean,
    isMetric?: boolean,
    numFilters?: number,
    searchParamsFilter?: $PropertyType<SearchParamsType, 'filter'>,
    siteName: string
};

type OwnProps = {
    results: {
        fromRecord?: number,
        page?: number,
        pageSize?: number,
        listings: ListingShape[],
        pinnedListing: ?PinnedListing,
        resultCount: number,
        queryUUID: string,
        percentBooked: PercentBookedResult
    },
    onScrollHitEnter: (number) => void,
    onHitMouseEnter: (string) => void,
    onHitMouseLeave: (MouseLeaveShape) => void
};

export const getDeviceType = (isTablet: boolean, isMobile: boolean) => {
    if (isTablet) {
        return 'tablet';
    }
    if (isMobile) {
        return 'mobile';
    }
    return 'desktop';
};

export type Props = OwnProps & StateProps & LinguaProps;

export const checkPriceless = (listings: ListingShape[]) => {
    const numPriceless = listings.reduce(
        (acc, l) => acc + (listingHasPrice(l) ? 0 : 1),
        0
    );
    const pricelesslabel: string = numPriceless.toString();
    if (numPriceless > 0) {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'no price presented',
            highfrequency: 'true',
            eventlabel: pricelesslabel
        });
    }

    const allPriceless = listings.reduce(
        (acc, l) => acc && !listingHasPrice(l),
        listings.length > 0
    );

    if (allPriceless) {
        reportError({name: 'priceless SERP'});
    }
};

export const checkDualPriceExistance = (listings: ListingShape[]) => {
    const listingsWithoutPricesCount = listings.filter(
        (listing) => !(listing.priceSummary && listing.priceSummarySecondary)
    ).length;

    if (listingsWithoutPricesCount === 0) {
        reportSuccess({
            name: 'priceSummary exists SERP',
            logData: {
                listingsCount: listings.length
            }
        });
    } else {
        reportError({
            name: 'priceSummary empty SERP',
            logData: {
                listingsWithoutPricesCount,
                listingsCount: listings.length
            }
        });
    }
};

export const checkDegradedHitsData = (
    listings: ListingShape[],
    isDesktop: boolean
) => {
    const {degradedHitsCount, missingData} = listings.reduce(
        (logData, listing) => {
            const dataErrors = hitDataErrors(listing, isDesktop);

            if (dataErrors.length) {
                logData.degradedHitsCount = ++logData.degradedHitsCount;

                dataErrors.forEach((error) => {
                    logData.missingData[error] =
                        (logData.missingData[error] || 0) + 1;
                });
            }
            return logData;
        },
        {
            degradedHitsCount: 0,
            missingData: {}
        }
    );

    if (degradedHitsCount > 0) {
        reportError({
            name: 'hits degraded',
            logData: {
                hitsCount: listings.length,
                degradedHitsCount,
                missingData,
                isDesktop
            }
        });
    }
};

export const checkListings = (
    listings: ListingShape[],
    isDesktop: boolean,
    isDatelessSearch: boolean
) => {
    checkPriceless(listings);
    checkDegradedHitsData(listings, isDesktop);
    if (!isDatelessSearch) {
        checkDualPriceExistance(listings);
    }
};

const flatten = (ary) => [].concat(...ary);

export class HitCollection extends Component<Props> {
    componentDidMount() {
        const {
            isDesktop,
            isDatelessSearch,
            results: {listings, queryUUID, percentBooked},
            inputParams
        } = this.props;

        if (listings && listings.length > 0) {
            checkListings(listings, isDesktop, isDatelessSearch);
            triggerSearchResultSetPresentedEdapEvent(
                inputParams,
                queryUUID,
                percentBooked
            );
        }
    }

    componentDidUpdate(prevProps: Props) {
        const {
            results: {listings: prevListings}
        } = prevProps;
        const {
            isDesktop,
            isDatelessSearch,
            results: {listings, queryUUID, percentBooked},
            inputParams
        } = this.props;

        if (prevListings && listings && isDifferent(listings, prevListings)) {
            checkListings(listings, isDesktop, isDatelessSearch);
            triggerSearchResultSetPresentedEdapEvent(
                inputParams,
                queryUUID,
                percentBooked
            );
        }
    }

    render() {
        const {
            onHitMouseEnter,
            onHitMouseLeave,
            onScrollHitEnter,
            results: {
                page,
                pageSize,
                listings: listingsCollection,
                pinnedListing,
                resultCount
            },
            breakpoint,
            havId,
            inputParams,
            isDatelessSearch,
            currency,
            isDesktop,
            isMobile,
            isTablet,
            isMetric,
            messages,
            isGoodBot,
            siteName
        } = this.props;
        const listings = mergeListingsCollectionWithPinnedListing(
            listingsCollection,
            pinnedListing
        );

        const pinnedListingId = pinnedListing
            ? pinnedListing.listing.listingId
            : '';

        const handleEnter = (label: number) => {
            onScrollHitEnter(label);
        };

        const handleScroll = () => handleEnter(0);
        return (
            <Timing measureName="hitCollection">
                <ScrollListener
                    useWindowAsScrollParent
                    onScroll={handleScroll}
                    data-wdio="ScrollListener"
                >
                    <div
                        className="HitCollection"
                        data-wdio="HitCollection"
                        role="list"
                    >
                        {flatten(
                            listings.map((listing, index) => {
                                const key = listing.listingId;
                                const hitProps = {
                                    havId,
                                    key,
                                    onHitClick: triggerListHitClickEDAPEvent,
                                    listing,
                                    hitIndex: index,
                                    page,
                                    pageSize,
                                    inputParams,
                                    isGoodBot,
                                    deviceType: getDeviceType(
                                        isTablet,
                                        isMobile
                                    ),
                                    isMultiUnitHit: !!(
                                        listing.multiUnitProperty &&
                                        listing.listingId !== pinnedListingId
                                    ),
                                    siteName
                                };

                                let hit = (
                                    <Hit
                                        {...hitProps}
                                        onMouseEnter={onHitMouseEnter}
                                        onMouseLeave={onHitMouseLeave}
                                    />
                                );

                                /**
                                 * BEGIN HIT WRAPPING
                                 */

                                if (listing.listingId === pinnedListingId) {
                                    hit = (
                                        <PinnedProperty
                                            key={key}
                                            headline={listing.headline}
                                            numResults={listings.length}
                                        >
                                            {hit}
                                        </PinnedProperty>
                                    );
                                }

                                // Adds waypoint instrumentation so we can fire
                                // callbacks when scrolling past hits 5 & 10 and
                                // send analytics events. These are legacy events.
                                // Can we stop firing them?
                                if ([4, 9].includes(index)) {
                                    hit = (
                                        <div
                                            data-wdio={`Waypoint${index + 1}`}
                                            key={key}
                                        >
                                            <Waypoint
                                                label={index + 1}
                                                onEnter={handleEnter}
                                                useWindowAsScrollParent
                                            >
                                                {hit}
                                            </Waypoint>
                                        </div>
                                    );
                                }

                                if (
                                    listing.multiUnitProperty &&
                                    listing.listingId !== pinnedListingId
                                ) {
                                    const multiUnitProps = {
                                        listing,
                                        hitIndex: index,
                                        page,
                                        pageSize,
                                        onMouseEnter: onHitMouseEnter,
                                        onMouseLeave: onHitMouseLeave,
                                        inputParams,
                                        isDesktop,
                                        currency,
                                        isMetric,
                                        isDatelessSearch,
                                        clientLogger: logger,
                                        messages
                                    };
                                    const handleMultiUnitHitExposed = () => {
                                        triggerMultiUnitHitExposedEDAPEvent({
                                            hitIndex: index,
                                            listing,
                                            page,
                                            pageSize
                                        });
                                    };

                                    hit = (
                                        <div
                                            data-wdio={`Waypoint${index + 1}`}
                                            key={key}
                                        >
                                            <Waypoint
                                                label={index + 1}
                                                onEnter={
                                                    handleMultiUnitHitExposed
                                                }
                                                useWindowAsScrollParent
                                            >
                                                <ErrorBoundary>
                                                    <MultiUnitHit
                                                        {...multiUnitProps}
                                                    >
                                                        {hit}
                                                    </MultiUnitHit>
                                                </ErrorBoundary>
                                            </Waypoint>
                                        </div>
                                    );
                                }

                                const serpHitPresented = () => {
                                    triggerSerpHitMabPresentedEdapEvent(
                                        listing,
                                        havId,
                                        siteName,
                                        getDeviceType(isTablet, isMobile)
                                    );
                                    triggerSerpHitPresentedEdapEvent(listing);
                                };

                                hit = (
                                    <div
                                        data-wdio={`Waypoint${index + 1}`}
                                        key={key}
                                    >
                                        <Waypoint
                                            label={index + 1}
                                            onEnter={serpHitPresented}
                                            useWindowAsScrollParent
                                        >
                                            <React.Fragment>
                                                {hit}
                                            </React.Fragment>
                                        </Waypoint>
                                    </div>
                                );
                                /**
                                 * END HIT WRAPPING.
                                 */

                                /**
                                 * Now, return either the hit or an array with
                                 * the hit + an inserted item like a banner.
                                 * Don't forget to add a key for inserted items!
                                 */
                                if (
                                    index ===
                                        MOBILE_DATES_PROMPT_BANNER_INDEX &&
                                    isDatelessSearch &&
                                    !media(breakpoint, {
                                        minWidth: 'SM_MIN'
                                    })
                                ) {
                                    return [
                                        hit,
                                        <DatesPromptBanner
                                            key={'dates-prompt-banner'}
                                            useXsImage
                                        />
                                    ];
                                }

                                if (index === 4)
                                    return [
                                        hit,
                                        <Experiment
                                            name={HA_PTA_LANDING_CLADSONSERP}
                                            key={`${HA_PTA_LANDING_CLADSONSERP}-${index}`}
                                            variant={[1, 2]}
                                        >
                                            <MesoAdsCL position={index} />
                                        </Experiment>
                                    ];

                                if (index === 9)
                                    return [
                                        hit,
                                        <Experiment
                                            name={HA_PTA_LANDING_CLADSONSERP}
                                            key={`${HA_PTA_LANDING_CLADSONSERP}-${index}`}
                                            variant={2}
                                        >
                                            <MesoAdsCL position={index} />
                                        </Experiment>
                                    ];

                                if (index === 14)
                                    return [
                                        hit,
                                        <Fragment>
                                            <Experiment
                                                name={
                                                    HA_PTA_LANDING_CLADSONSERP
                                                }
                                                key={`${HA_PTA_LANDING_CLADSONSERP}-${index}`}
                                                variant={2}
                                            >
                                                <MesoAdsCL position={index} />
                                            </Experiment>
                                            <LeadCarouselContainerWrapper
                                                placement={
                                                    LEAD_CAROUSEL_PLACEMENT.MIDDLE
                                                }
                                                resultCount={resultCount}
                                            />
                                        </Fragment>
                                    ];

                                /**
                                 * If no inserted items, just return the hit
                                 */
                                return hit;
                            })
                        )}
                    </div>
                </ScrollListener>
            </Timing>
        );
    }
}

const mapStateToProps = ({
    viewport: {breakpoint},
    system: {
        currency,
        directEntry,
        fullLocale,
        havId,
        isDesktop,
        isMobile,
        isTablet,
        isGoodBot,
        isMetric,
        siteName
    },
    searchParams: {params, numFilters, inputParams}
}: ReduxState): StateProps => {
    return {
        breakpoint,
        currency,
        directEntry,
        fetchImages: params.fetchImages,
        havId,
        fullLocale,
        inputParams,
        isDatelessSearch: isDateless(params),
        isDesktop,
        isMobile,
        isTablet,
        isGoodBot,
        isMetric,
        numFilters,
        searchParamsFilter: params.filter,
        siteName
    };
};

export const ConnectedHitCollection = compose(
    connect(mapStateToProps),
    withPAR,
    withLingua({
        namespace: 'traveler/search',
        category: 'messages'
    })
)(HitCollection);

const HitCollectionWithHelmet = (props: OwnProps) => {
    const {listings: listingsCollection, pinnedListing} = props.results;
    const listings = mergeListingsCollectionWithPinnedListing(
        listingsCollection,
        pinnedListing
    );

    return (
        <Fragment>
            {listings && (
                <Helmet>
                    <style>
                        {listings
                            .slice(0, MAX_PRELOAD_HIT_THUMBNAIL)
                            .filter(
                                (listing) =>
                                    !!(listing.images && listing.images[0])
                            )
                            .map((listing, index) => {
                                // Flow ignores check above
                                // $FlowFixMe
                                const url = listing.images[0].c6_uri;
                                return url
                                    ? `.${hitThumbnailBackgroundImageClass(
                                          index
                                      )} {background-image: url("${url}");}`
                                    : '';
                            })
                            .join(' ')}
                    </style>
                </Helmet>
            )}
            <ConnectedHitCollection {...props} />
        </Fragment>
    );
};

export default HitCollectionWithHelmet;
